import { useSetAtom } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'
import * as R from 'remeda'

import { FlickeringBadge } from '~/components/elements/FlickeringBadge'
import { HStack } from '~/components/elements/HStack'
import { IconButton } from '~/components/elements/IconButton'
import { StyledButton } from '~/components/elements/StyledButton'
import { Typography } from '~/components/elements/Typography'

import { dialogInfoAtom } from '../atoms'
import { type SurveyGroup } from '../hooks/useMainQuery'

import styles from './SurveyGroupListItem.css'

export type SurveyGroupListItemProps = {
  surveyGroup: SurveyGroup
}

export const SurveyGroupListItem = ({
  surveyGroup,
}: SurveyGroupListItemProps) => {
  const { t } = useTranslation()
  const setDialogInfo = useSetAtom(dialogInfoAtom)

  const handlerMap = React.useMemo(
    () =>
      R.mapToObj(
        [
          'delete',
          'update',
          'surveySettings',
          'brandSettings',
          'questionSettings',
          'customerAttributeGroupSettings',
        ] as const,
        (dialogType) => [
          dialogType,
          () => {
            setDialogInfo({ dialogType, surveyGroup })
          },
        ],
      ),
    [setDialogInfo, surveyGroup],
  )

  return (
    <HStack align="center" className={styles.root} padding={2} spacing={2}>
      <HStack align="center" flex="none" spacing={0}>
        <HStack spacing={0}>
          <IconButton
            buttonStyle="light"
            fillMode="flat"
            icon="delete"
            onClick={handlerMap['delete']}
          />

          <IconButton
            buttonStyle="light"
            fillMode="flat"
            icon="edit"
            onClick={handlerMap['update']}
          />
        </HStack>

        <Typography variant="heading3">{surveyGroup.title}</Typography>
      </HStack>

      <HStack align="center" flex="none" spacing={2}>
        <StyledButton fillMode="outline" onClick={handlerMap['surveySettings']}>
          {t('survey_settings')}

          {surveyGroup.alertSurveys && <FlickeringBadge color="error" />}
        </StyledButton>

        <StyledButton fillMode="outline" onClick={handlerMap['brandSettings']}>
          {t('brand_settings')}
        </StyledButton>

        <StyledButton
          fillMode="outline"
          onClick={handlerMap['questionSettings']}
        >
          {t('questionnaire_settings')}
        </StyledButton>

        <StyledButton
          fillMode="outline"
          onClick={handlerMap['customerAttributeGroupSettings']}
        >
          {t('customized_segments_settings_1')}
        </StyledButton>
      </HStack>
    </HStack>
  )
}
